import { AlignCenter, AlignLeft, AlignRight, AlignVerticalJustifyCenter, AlignVerticalJustifyEnd, AlignVerticalJustifyStart, ArrowLeft, ArrowRight, Bold, BookmarkPlus, BringToFront, Circle, Copy, Group, Italic, MousePointer2, MoveHorizontal, Pencil, RotateCcw, RotateCw, SendToBack, Slash, Square, Strikethrough, Trash2, Type, Underline, Ungroup } from 'lucide-react';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { AppContext } from '@r-app/app-context';
import { DisplayMode } from '@r-core/controller';
import { SkuAnnotationArrowhead, SkuAnnotationLineStyle, SkuAnnotationType, type SkuAnnotation, type SkuAnnotations, useSubscription } from '@rta/rta-blueprint-library';

import { ANNOTATION_FONT_FAMILIES, AnnotationPaintControl, TOOLBAR_ICON_SIZE, ToolbarButton, ToolbarCollapseTab, ToolbarGroup, ToolbarPopover, ToolbarStepper, toolbarInputStyle, toolbarPanelHeadingStyle, toolbarPanelRowStyle, toolbarPropertyRowStyle, toolbarReadoutStyle, toolbarRowStyle, toolbarShellStyle, toolbarSurfaceStyle } from './toolbar';
import { applyAnnotationBoxPatch, DEFAULT_ANNOTATION_FILL, getAnnotationCapabilities, getRangeStyle, normalizeAnnotationFontSize, resolveAnnotationStyleRuns, STYLE_RUN_PROPERTIES, type AnnotationPreviewPatch, type EffectiveTextStyle, type StyleRunPatch } from './helpers';
import { toAnnotationPresetRecords } from '../blueprint-view/helpers/place-annotation-preset';
import { raceFrame } from '../blueprint-view/helpers/race-frame';
import { useSkuAnnotation, type AnnotationStylePreview } from './sku-annotation-context';

export type SkuAnnotationTool = 'select' | 'textbox' | 'doodle' | 'line' | 'rectangle' | 'ellipse';

const FONT_SIZE_STEP = 0.25;
const MIN_FONT_SIZE = 0.5;
const MAX_FONT_SIZE = 6;

const STROKE_WIDTH_STEP = 0.1;
/** 0 IS a weight: it is the off state the none swatch writes, and stepping up from it is how the line comes back */
const MIN_STROKE_WIDTH = 0;
const MAX_STROKE_WIDTH = 3;

const ROTATION_STEP = 15;

const TOOL_OPTIONS: Array<{ tool: SkuAnnotationTool; label: string; title: string; icon: React.ReactNode }> = [
	{ tool: 'select', label: 'Select', title: 'Select / move', icon: <MousePointer2 size={TOOLBAR_ICON_SIZE} /> },
	{ tool: 'textbox', label: 'Text', title: 'Add a text box', icon: <Type size={TOOLBAR_ICON_SIZE} /> },
	{ tool: 'doodle', label: 'Draw', title: 'Freehand draw', icon: <Pencil size={TOOLBAR_ICON_SIZE} /> },
	{ tool: 'line', label: 'Line', title: 'Straight line', icon: <Slash size={TOOLBAR_ICON_SIZE} /> },
	{ tool: 'rectangle', label: 'Rect', title: 'Rectangle', icon: <Square size={TOOLBAR_ICON_SIZE} /> },
	{ tool: 'ellipse', label: 'Ellipse', title: 'Ellipse', icon: <Circle size={TOOLBAR_ICON_SIZE} /> }
];

const TEXT_ALIGN_OPTIONS: Array<{ value: string; title: string; icon: React.ReactNode }> = [
	{ value: 'left', title: 'Align left', icon: <AlignLeft size={TOOLBAR_ICON_SIZE} /> },
	{ value: 'center', title: 'Align center', icon: <AlignCenter size={TOOLBAR_ICON_SIZE} /> },
	{ value: 'right', title: 'Align right', icon: <AlignRight size={TOOLBAR_ICON_SIZE} /> }
];

const VERTICAL_ALIGN_OPTIONS: Array<{ value: string; title: string; icon: React.ReactNode }> = [
	{ value: 'top', title: 'Align top', icon: <AlignVerticalJustifyStart size={TOOLBAR_ICON_SIZE} /> },
	{ value: 'middle', title: 'Align middle', icon: <AlignVerticalJustifyCenter size={TOOLBAR_ICON_SIZE} /> },
	{ value: 'bottom', title: 'Align bottom', icon: <AlignVerticalJustifyEnd size={TOOLBAR_ICON_SIZE} /> }
];

const LINE_STYLE_OPTIONS: Array<{ style: SkuAnnotationLineStyle; title: string; dash?: string }> = [
	{ style: SkuAnnotationLineStyle.Solid, title: 'Solid' },
	{ style: SkuAnnotationLineStyle.Dashed, title: 'Dashed', dash: '4,3' },
	{ style: SkuAnnotationLineStyle.Dotted, title: 'Dotted', dash: '1,3' }
];

const ARROWHEAD_OPTIONS: Array<{ arrowhead: SkuAnnotationArrowhead; title: string; icon: React.ReactNode }> = [
	{ arrowhead: SkuAnnotationArrowhead.None, title: 'No arrows', icon: <Slash size={TOOLBAR_ICON_SIZE} /> },
	{ arrowhead: SkuAnnotationArrowhead.End, title: 'Arrow at the end', icon: <ArrowRight size={TOOLBAR_ICON_SIZE} /> },
	{ arrowhead: SkuAnnotationArrowhead.Start, title: 'Arrow at the start', icon: <ArrowLeft size={TOOLBAR_ICON_SIZE} /> },
	{ arrowhead: SkuAnnotationArrowhead.Both, title: 'Arrows on both ends', icon: <MoveHorizontal size={TOOLBAR_ICON_SIZE} /> }
];

/** Three rules of increasing thickness -- reads as "how heavy is the line", which a pencil never did */
const StrokeWeightGlyph = (): React.ReactElement => (
	<svg width={TOOLBAR_ICON_SIZE + 3} height={TOOLBAR_ICON_SIZE} viewBox="0 0 18 15" aria-hidden="true">
		<line x1="1" y1="3.5" x2="17" y2="3.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
		<line x1="1" y1="7.5" x2="17" y2="7.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
		<line x1="1" y1="12" x2="17" y2="12" stroke="currentColor" strokeWidth="3.6" strokeLinecap="round" />
	</svg>
);

type MergeOptimisticStyleParams = {
	base: EffectiveTextStyle;
	patch: StyleRunPatch | null;
};

/** Lets a format button light up on click even though the live editor holds its runs locally until commit */
const mergeOptimisticStyle = (params: MergeOptimisticStyleParams): EffectiveTextStyle => {
	const { base, patch } = params;
	if (!patch) return base;

	const merged: Record<string, unknown> = { ...base };
	STYLE_RUN_PROPERTIES.forEach((property) => {
		const value = patch[property];
		if (typeof value === 'undefined') return;
		if (value === null) delete merged[property];
		else merged[property] = value;
	});
	return merged as EffectiveTextStyle;
};

export type SkuAnnotationToolbarProps = {
	activeTool: SkuAnnotationTool;
	onToolChange: (tool: SkuAnnotationTool) => void;
	selectedAnnotation?: SkuAnnotation | null;
	/** The collection the selection lives in, so z-order / group / duplicate / delete work from the toolbar */
	annotations?: SkuAnnotations | null;
};

export const SkuAnnotationToolbar = (props: SkuAnnotationToolbarProps): React.ReactElement => {
	const { activeTool, onToolChange, selectedAnnotation = null, annotations = null } = props;
	const context = useContext(AppContext);
	const controller = context?.controller;
	const {
		setEditingAnnotationId,
		formatSelection,
		applyStyleRef,
		liveStyleRunsRef,
		toolbarInteractingRef,
		setStylePreview,
		selectedAnnotationIds,
		setSelectedAnnotationId,
		setSelectedAnnotationIds,
		pendingSaveRef,
		onSaveAnnotationPreset
	} = useSkuAnnotation();

	const [collapsed, setCollapsed] = useState(false);
	const [openPopover, setOpenPopover] = useState<string | null>(null);
	const [optimistic, setOptimistic] = useState<{ key: string; patch: StyleRunPatch } | null>(null);
	const [presetName, setPresetName] = useState('Untitled preset');
	const previewRef = useRef<AnnotationStylePreview>(null);
	const previewScheduledRef = useRef(false);

	useSubscription(controller, false, [['navigation-display-mode', '*']]);
	useSubscription(controller?.kitchen ?? null, false, [['sku_annotation', 'updated'], ['sku_annotations', '*']]);

	const selectedId = selectedAnnotation?.id ?? null;

	useEffect(() => {
		setOpenPopover(null);
		setOptimistic(null);
	}, [selectedId]);

	useEffect(() => () => setStylePreview(null), [setStylePreview]);

	// One rAF per burst of picker frames, into a ref-backed store: the preview writes neither the model nor the context value, so only the previewed annotation re-renders
	const schedulePreview = useCallback((patch: AnnotationPreviewPatch | null): void => {
		if (!selectedId) return;
		previewRef.current = patch ? { annotationId: selectedId, patch, range: null } : null;
		if (previewScheduledRef.current) return;
		previewScheduledRef.current = true;
		raceFrame(() => {
			previewScheduledRef.current = false;
			setStylePreview(previewRef.current);
		});
	}, [selectedId, setStylePreview]);

	// Keeping focus in the annotation's textarea is what makes a per-selection format possible; only controls that must open a native picker are exempt
	const handleShellMouseDown = useCallback((e: React.MouseEvent): void => {
		toolbarInteractingRef.current = true;
		if (!(e.target as HTMLElement).closest('[data-native-focus]')) e.preventDefault();
		const release = (): void => {
			document.removeEventListener('mouseup', release, true);
			raceFrame(() => { toolbarInteractingRef.current = false; });
		};
		document.addEventListener('mouseup', release, true);
	}, [toolbarInteractingRef]);

	const isPageAnnotation = (selectedAnnotation?.pageNumber ?? 0) > 0;
	const toDisplayFontSize = useCallback((raw: number): number => (
		isPageAnnotation ? raw : Number(normalizeAnnotationFontSize(raw).toFixed(2))
	), [isPageAnnotation]);

	const hasRange = Boolean(selectedAnnotation && formatSelection && formatSelection.annotationId === selectedAnnotation.id && formatSelection.end > formatSelection.start);
	const formatKey = selectedId ? `${selectedId}:${hasRange && formatSelection ? `${formatSelection.start}-${formatSelection.end}` : 'box'}` : '';
	const optimisticPatch = optimistic && optimistic.key === formatKey ? optimistic.patch : null;

	const rangeStyle = useMemo((): EffectiveTextStyle => {
		if (!selectedAnnotation || !hasRange || !formatSelection) return {};
		// A live editor holds its runs locally until commit, so its uncommitted runs -- not the model -- are what the buttons must reflect
		const live = liveStyleRunsRef.current;
		const runs = live && live.annotationId === selectedAnnotation.id ? live.runs : resolveAnnotationStyleRuns(selectedAnnotation);
		return getRangeStyle({ runs, start: formatSelection.start, end: formatSelection.end });
	}, [selectedAnnotation, hasRange, formatSelection, liveStyleRunsRef]);

	const effectiveStyle = useMemo(() => mergeOptimisticStyle({ base: rangeStyle, patch: optimisticPatch }), [rangeStyle, optimisticPatch]);

	const applyPatch = useCallback((patch: StyleRunPatch): void => {
		if (!selectedAnnotation) return;
		setOptimistic((previous) => ({ key: formatKey, patch: { ...(previous && previous.key === formatKey ? previous.patch : {}), ...patch } }));
		if (applyStyleRef.current) {
			applyStyleRef.current(patch);
			return;
		}
		const runs = applyAnnotationBoxPatch({
			annotation: selectedAnnotation,
			patch,
			runs: resolveAnnotationStyleRuns(selectedAnnotation),
			textLength: selectedAnnotation.text.length
		});
		if (selectedAnnotation.text.length === 0) return;
		selectedAnnotation.styleRuns = runs;
		if (selectedAnnotation.colorRuns.length > 0) selectedAnnotation.colorRuns = [];
	}, [selectedAnnotation, formatKey, applyStyleRef]);

	const displayMode = controller?.navigation?.displayMode;
	const isAnnotationMode = displayMode === DisplayMode.SkuOverhead
		|| displayMode === DisplayMode.SkuOverheadManufacturing
		|| displayMode === DisplayMode.BlueprintView;

	if (!controller || !isAnnotationMode) return <></>;

	if (collapsed) {
		return (
			<div data-sku-toolbar="true" style={{ ...toolbarShellStyle, pointerEvents: 'none' }}>
				<ToolbarCollapseTab isCollapsed onToggle={() => setCollapsed(false)} />
			</div>
		);
	}

	const capabilities = getAnnotationCapabilities(selectedAnnotation?.type);
	const { text: canFormatText, fill: canFill, stroke: canStroke, strokeWeight: canWeighStroke, arrowheads: hasArrowheads, rotate: canRotate } = capabilities;

	const fontSizeStep = isPageAnnotation ? 1 : FONT_SIZE_STEP;
	const fontSizeMin = isPageAnnotation ? 6 : MIN_FONT_SIZE;
	const fontSizeMax = isPageAnnotation ? 72 : MAX_FONT_SIZE;

	const boxFontSize = selectedAnnotation ? toDisplayFontSize(selectedAnnotation.fontSize) : 1.5;
	const activeFontSize = typeof effectiveStyle.fontSize === 'number' ? toDisplayFontSize(effectiveStyle.fontSize) : boxFontSize;
	const activeFontFamily = effectiveStyle.fontFamily ?? selectedAnnotation?.fontFamily ?? 'Arial';
	const decorations = (effectiveStyle.textDecoration ?? '').split(' ').filter(Boolean);

	const targetIds = selectedAnnotationIds.length > 0
		? selectedAnnotationIds
		: selectedId ? [selectedId] : [];
	const targets = annotations
		? targetIds.map((id) => annotations.findById(id)).filter((a): a is SkuAnnotation => a !== null)
		: [];
	const canArrange = targets.length > 0;
	const groupedCount = targets.filter((a) => a.groupId !== '').length;

	const popoverProps = (id: string): { isOpen: boolean; onOpenChange: (isOpen: boolean) => void } => ({
		isOpen: openPopover === id,
		onOpenChange: (isOpen: boolean) => setOpenPopover(isOpen ? id : null)
	});

	const toggleDecoration = (token: string): void => {
		const next = decorations.includes(token) ? decorations.filter((entry) => entry !== token) : [...decorations, token];
		applyPatch({ textDecoration: next.length > 0 ? next.join(' ') : null });
	};

	const setBoxAlign = (property: 'textAlign' | 'verticalAlign', value: string): void => {
		const boxes = targets.length > 0 ? targets : selectedAnnotation ? [selectedAnnotation] : [];
		boxes.filter((a) => a.type === SkuAnnotationType.TextBox).forEach((a) => { a[property] = value; });
	};

	const handleBringToFront = (): void => { targetIds.forEach((id) => annotations?.moveToFront(id)); };
	const handleSendToBack = (): void => { [...targetIds].reverse().forEach((id) => annotations?.moveToBack(id)); };

	const handleGroup = (): void => {
		if (targets.length < 2) return;
		const groupId = crypto.randomUUID();
		targets.forEach((member) => { member.groupId = groupId; });
	};

	const handleUngroup = (): void => {
		targets.forEach((member) => { member.groupId = ''; });
	};

	const handleDuplicate = (): void => {
		if (!annotations || targets.length === 0) return;
		const offset = isPageAnnotation ? 12 : 1;
		const newIds = targets.map((member) => {
			const isLineMember = member.type === SkuAnnotationType.Line;
			return annotations.add({
				...member.serialize(),
				id: '',
				groupId: '',
				createDate: undefined,
				x: member.x + offset,
				y: member.y + offset,
				x2: isLineMember ? member.x2 + offset : 0,
				y2: isLineMember ? member.y2 + offset : 0
			});
		});
		setSelectedAnnotationIds(newIds);
	};

	const handleDelete = (): void => {
		targetIds.forEach((id) => annotations?.delete(id));
		setSelectedAnnotationId(null);
		setEditingAnnotationId(null);
	};

	// Rebasing and unit conversion live in place-annotation-preset, so what is saved cannot drift from where it lands
	const handleSaveToPresets = (): void => {
		const name = presetName.trim();
		if (!onSaveAnnotationPreset || targets.length === 0 || !name) return;
		// The live editor keeps its text and runs local until it commits, so serializing first would save a blank text box
		pendingSaveRef.current?.();
		onSaveAnnotationPreset(name, toAnnotationPresetRecords({ members: targets, isSkuSurface: annotations === controller.kitchen?.skuAnnotations }));
		setPresetName('Untitled preset');
		setOpenPopover(null);
	};

	return (
		<div data-sku-toolbar="true" style={toolbarShellStyle} onMouseDown={handleShellMouseDown}>
			<div style={toolbarSurfaceStyle}>
				<div style={toolbarRowStyle}>
					<ToolbarGroup label="Add" withSeparator={false}>
						{TOOL_OPTIONS.map(({ tool, label, title, icon }) => (
							<ToolbarButton key={tool} title={title} label={label} isActive={activeTool === tool} onClick={() => onToolChange(tool)}>
								{icon}
							</ToolbarButton>
						))}
					</ToolbarGroup>

					{canArrange && (
						<ToolbarGroup label={`Arrange (${targets.length} selected)`}>
							<ToolbarButton title="Draw this on top of the others" label="Front" onClick={handleBringToFront}>
								<BringToFront size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
							<ToolbarButton title="Draw this behind the others" label="Back" onClick={handleSendToBack}>
								<SendToBack size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
							<ToolbarButton title="Lock the selected items together so they move as one" label="Group" disabled={targets.length < 2} onClick={handleGroup}>
								<Group size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
							<ToolbarButton title="Split a group back into separate items" label="Ungroup" disabled={groupedCount === 0} onClick={handleUngroup}>
								<Ungroup size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
							<ToolbarButton title="Make a copy just below this one" label="Copy" onClick={handleDuplicate}>
								<Copy size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
							{onSaveAnnotationPreset && (
								<ToolbarPopover
									{...popoverProps('preset')}
									title="Save the selection as a reusable preset"
									label="Preset"
									panelWidth={196}
									align="right"
									trigger={<BookmarkPlus size={TOOLBAR_ICON_SIZE} />}
								>
									<div style={toolbarPanelHeadingStyle}>Save as preset</div>
									<input
										data-native-focus="true"
										value={presetName}
										placeholder="Preset name"
										onChange={(e) => setPresetName(e.target.value)}
										onKeyDown={(e) => { if (e.key === 'Enter') handleSaveToPresets(); }}
										style={{ ...toolbarInputStyle, width: '100%' }}
									/>
									<div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
										<ToolbarButton title="Save this preset" label="Save" disabled={!presetName.trim()} onClick={handleSaveToPresets}>
											<BookmarkPlus size={TOOLBAR_ICON_SIZE} />
										</ToolbarButton>
									</div>
								</ToolbarPopover>
							)}
							<ToolbarButton title="Delete the selection" label="Delete" tone="danger" onClick={handleDelete}>
								<Trash2 size={TOOLBAR_ICON_SIZE} />
							</ToolbarButton>
						</ToolbarGroup>
					)}
				</div>

				{selectedAnnotation && (
					<div style={toolbarPropertyRowStyle}>
						{canFormatText && (
							<ToolbarGroup label={hasRange ? 'Text — selected text only' : 'Text — whole box'} withSeparator={false}>
								<ToolbarPopover
									{...popoverProps('font')}
									title="Font family"
									label={activeFontFamily}
									panelWidth={168}
									trigger={<Type size={TOOLBAR_ICON_SIZE} />}
								>
									<div style={toolbarPanelHeadingStyle}>Font</div>
									{ANNOTATION_FONT_FAMILIES.map((family) => (
										<button
											key={family}
											type="button"
											onClick={() => { applyPatch({ fontFamily: family }); setOpenPopover(null); }}
											style={{ ...toolbarPanelRowStyle(family === activeFontFamily), fontFamily: `${family}, sans-serif` }}
										>
											{family}
										</button>
									))}
								</ToolbarPopover>

								<ToolbarStepper
									title="font size"
									label="Size"
									value={activeFontSize}
									min={fontSizeMin}
									max={fontSizeMax}
									step={fontSizeStep}
									onChange={(value) => applyPatch({ fontSize: value })}
								/>

								<ToolbarButton title="Bold" isActive={(effectiveStyle.fontWeight ?? selectedAnnotation.fontWeight) === 'bold'} onClick={() => applyPatch({ fontWeight: (effectiveStyle.fontWeight ?? selectedAnnotation.fontWeight) === 'bold' ? 'normal' : 'bold' })}>
									<Bold size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
								<ToolbarButton title="Italic" isActive={(effectiveStyle.fontStyle ?? selectedAnnotation.fontStyle) === 'italic'} onClick={() => applyPatch({ fontStyle: (effectiveStyle.fontStyle ?? selectedAnnotation.fontStyle) === 'italic' ? 'normal' : 'italic' })}>
									<Italic size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
								<ToolbarButton title="Underline" isActive={decorations.includes('underline')} onClick={() => toggleDecoration('underline')}>
									<Underline size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
								<ToolbarButton title="Strikethrough" isActive={decorations.includes('line-through')} onClick={() => toggleDecoration('line-through')}>
									<Strikethrough size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>

								<AnnotationPaintControl {...popoverProps('textColor')} channel="text" annotation={selectedAnnotation} style={effectiveStyle} applyPatch={applyPatch} onPreview={schedulePreview} />
								<AnnotationPaintControl {...popoverProps('highlight')} channel="highlight" annotation={selectedAnnotation} style={effectiveStyle} applyPatch={applyPatch} onPreview={schedulePreview} />
							</ToolbarGroup>
						)}

						{selectedAnnotation.type === SkuAnnotationType.TextBox && (
							<ToolbarGroup label="Align" withSeparator={canFormatText}>
								{TEXT_ALIGN_OPTIONS.map(({ value, title, icon }) => (
									<ToolbarButton key={value} title={title} isActive={(selectedAnnotation.textAlign || 'left') === value} onClick={() => setBoxAlign('textAlign', value)}>
										{icon}
									</ToolbarButton>
								))}
								{VERTICAL_ALIGN_OPTIONS.map(({ value, title, icon }) => (
									<ToolbarButton key={value} title={title} isActive={(selectedAnnotation.verticalAlign || 'top') === value} onClick={() => setBoxAlign('verticalAlign', value)}>
										{icon}
									</ToolbarButton>
								))}
							</ToolbarGroup>
						)}

						{canFill && (
							<ToolbarGroup label="Fill" withSeparator={canFormatText}>
								<AnnotationPaintControl {...popoverProps('fill')} channel="fill" annotation={selectedAnnotation} style={effectiveStyle} applyPatch={applyPatch} onPreview={schedulePreview} />
								<ToolbarStepper
									title="fill opacity"
									label="Opacity"
									format={(value) => `${Math.round(value * 100)}%`}
									value={selectedAnnotation.fillOpacity}
									min={0}
									max={1}
									step={0.1}
									precision={1}
									onChange={(value) => {
										selectedAnnotation.fillOpacity = value;
										if (value > 0 && !selectedAnnotation.backgroundColor) selectedAnnotation.backgroundColor = DEFAULT_ANNOTATION_FILL;
									}}
								/>
							</ToolbarGroup>
						)}

						{canStroke && (
							<ToolbarGroup label={canFill ? 'Border' : 'Line'} withSeparator={canFormatText || canFill}>
								<AnnotationPaintControl {...popoverProps('strokeColor')} channel="stroke" annotation={selectedAnnotation} style={effectiveStyle} applyPatch={applyPatch} onPreview={schedulePreview} />

								<ToolbarPopover
									{...popoverProps('stroke')}
									title={canWeighStroke ? 'Line weight and style' : 'Border style'}
									label={canWeighStroke ? `Weight ${selectedAnnotation.strokeWidth.toFixed(1)}` : 'Style'}
									panelWidth={168}
									align="right"
									trigger={<StrokeWeightGlyph />}
								>
									{canWeighStroke && (
										<>
											<div style={toolbarPanelHeadingStyle}>Weight</div>
											<div style={{ display: 'flex', alignItems: 'center', gap: '2px', marginBottom: '8px' }}>
												<ToolbarStepper
													title="line weight"
													value={selectedAnnotation.strokeWidth}
													min={MIN_STROKE_WIDTH}
													max={MAX_STROKE_WIDTH}
													step={STROKE_WIDTH_STEP}
													precision={1}
													onChange={(value) => { selectedAnnotation.strokeWidth = value; }}
												/>
											</div>
										</>
									)}
									<div style={toolbarPanelHeadingStyle}>Style</div>
									<div style={{ display: 'flex', gap: '2px' }}>
										{LINE_STYLE_OPTIONS.map(({ style, title, dash }) => (
											<ToolbarButton
												key={style}
												title={title}
												isActive={selectedAnnotation.lineStyle === style}
												onClick={() => { selectedAnnotation.lineStyle = style; }}
											>
												<svg width="22" height="10" viewBox="0 0 22 10" aria-hidden="true">
													<line x1="1" y1="5" x2="21" y2="5" stroke="currentColor" strokeWidth="2" strokeDasharray={dash} strokeLinecap={dash === '1,3' ? 'round' : undefined} />
												</svg>
											</ToolbarButton>
										))}
									</div>
								</ToolbarPopover>

								{hasArrowheads && (
									<ToolbarPopover
										{...popoverProps('arrows')}
										title="Arrowheads"
										label="Arrows"
										panelWidth={148}
										align="right"
										trigger={<ArrowRight size={TOOLBAR_ICON_SIZE} />}
									>
										<div style={toolbarPanelHeadingStyle}>Arrowheads</div>
										<div style={{ display: 'flex', gap: '2px' }}>
											{ARROWHEAD_OPTIONS.map(({ arrowhead, title, icon }) => (
												<ToolbarButton
													key={arrowhead}
													title={title}
													isActive={selectedAnnotation.arrowhead === arrowhead}
													onClick={() => { selectedAnnotation.arrowhead = arrowhead; }}
												>
													{icon}
												</ToolbarButton>
											))}
										</div>
									</ToolbarPopover>
								)}
							</ToolbarGroup>
						)}

						{canRotate && (
							<ToolbarGroup label="Rotate">
								<ToolbarButton title="Rotate 15° left" onClick={() => { selectedAnnotation.rotation = (selectedAnnotation.rotation - ROTATION_STEP + 360) % 360; }}>
									<RotateCcw size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
								<span style={toolbarReadoutStyle}>{`${Math.round(selectedAnnotation.rotation)}°`}</span>
								<ToolbarButton title="Rotate 15° right" onClick={() => { selectedAnnotation.rotation = (selectedAnnotation.rotation + ROTATION_STEP) % 360; }}>
									<RotateCw size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
								<ToolbarButton title="Reset the rotation to 0°" label="Reset" disabled={Math.round(selectedAnnotation.rotation) === 0} onClick={() => { selectedAnnotation.rotation = 0; }}>
									<RotateCcw size={TOOLBAR_ICON_SIZE} />
								</ToolbarButton>
							</ToolbarGroup>
						)}

					</div>
				)}
			</div>

			<ToolbarCollapseTab isCollapsed={false} onToggle={() => setCollapsed(true)} />
		</div>
	);
};
